import type { 
  AISettings, 
  AIAnalysisRequest, 
  AIAnalysisResult
} from '$lib/types/ai'
import type { GrammarError, AIComment } from '$lib/types/grammar'
import { AIAnalysisService } from './AIAnalysisService'
import { 
  ServiceError, 
  NetworkError, 
  RateLimitError,
  ValidationError,
  AIProviderError
} from '../errors/ServiceError'
import { serviceLogger } from '../errors/ServiceLogger'
import { RetryStrategy } from '../errors/RetryStrategy'

/**
 * Enhanced AI Analysis Service with improved error handling
 * 
 * This is a backward-compatible wrapper around the existing AIAnalysisService
 * that adds structured error handling, logging, and retry logic.
 * 
 * Migration approach:
 * 1. Extends existing service to maintain compatibility
 * 2. Overrides methods to add new error handling
 * 3. Gradually move logic from parent to this class
 */
export class AIAnalysisServiceV2 extends AIAnalysisService {
  private readonly logger = serviceLogger
  private hasInitialized = false
  
  constructor(settings: AISettings) {
    super(settings)
    // Attempt to initialize client immediately
    this.initializeClient().catch(() => {
      // Silent fail - will retry on first use
    })
  }
  
  /**
   * Override updateSettings to ensure client is reinitialized
   */
  updateSettings(settings: AISettings): void {
    super.updateSettings(settings)
    // Reinitialize client with new settings
    this.initializeClient().catch(() => {
      // Silent fail - will retry on first use
    })
  }
  
  /**
   * Enhanced analyzeText with retry logic and structured errors
   */
  async analyzeText(
    text: string,
    useStreaming: boolean = false
  ): Promise<{ errors: GrammarError[], comments: Map<string, AIComment> }> {
    
    try {
      const result = await RetryStrategy.execute(
        async () => {
          try {
            return await super.analyzeText(text, useStreaming)
          } catch (error: any) {
            throw this.convertToStructuredError(error)
          }
        },
        {
          maxAttempts: 3,
          initialDelay: 1000,
          shouldRetry: (error) => {
            // Retry on network errors and rate limits
            if (error instanceof NetworkError) return true
            if (error instanceof RateLimitError) return true
            if (error instanceof AIProviderError && error.recoverable) return true
            
            // Don't retry on validation errors
            if (error instanceof ValidationError) return false
            
            return false
          },
          onRetry: (error, attempt) => {
            this.logger.warn(`AI analysis retry attempt ${attempt}`, {
              error: error.message,
              code: error.code,
              textLength: text.length
            })
          }
        }
      )
      
      return result
      
    } catch (error: any) {
      this.logger.error('AI text analysis failed after retries', error, {
        textLength: text.length,
        useStreaming
      })
      
      if (error instanceof ServiceError) {
        throw error
      }
      
      throw new ServiceError(
        error.message || 'AI 텍스트 분석 중 오류가 발생했습니다',
        'AI_ANALYSIS_FAILED',
        { originalError: error }
      )
    }
  }
  
  /**
   * Enhanced analyzeGrammarError with retry logic
   */
  async analyzeGrammarError(
    error: GrammarError, 
    contextText: string, 
    useStreaming: boolean = false
  ): Promise<AIComment> {
    // Ensure client is initialized before attempting analysis
    if (!this.hasInitialized) {
      try {
        await this.initializeClient()
        this.hasInitialized = true
      } catch (initError) {
        // Silent fail - will throw error below
      }
    }
    
    try {
      const result = await RetryStrategy.execute(
        async () => {
          try {
            return await super.analyzeGrammarError(error, contextText, useStreaming)
          } catch (error: any) {
            throw this.convertToStructuredError(error)
          }
        },
        {
          maxAttempts: 2,
          initialDelay: 500,
          shouldRetry: (error) => {
            if (error instanceof NetworkError) return true
            if (error instanceof RateLimitError) return true
            return false
          }
        }
      )
      
      return result
      
    } catch (error: any) {
      // Only log unexpected errors
      if (!(error instanceof AIProviderError) && !(error instanceof ServiceError)) {
        this.logger.error('AI error analysis failed', error)
      }
      
      if (error instanceof ServiceError) {
        throw error
      }
      
      throw new AIProviderError(
        'AI 오류 분석 중 문제가 발생했습니다',
        'ANALYSIS_FAILED',
        { originalError: error }
      )
    }
  }
  
  /**
   * Enhanced analyzeCorrections with structured error handling
   */
  async analyzeCorrections(
    request: AIAnalysisRequest,
    useStreaming: boolean = false
  ): Promise<AIAnalysisResult[]> {
    // Validate request
    if (!request.corrections || request.corrections.length === 0) {
      throw new ValidationError(
        '분석할 교정 항목이 없습니다',
        'corrections',
        { request }
      )
    }
    
    try {
      const result = await RetryStrategy.execute(
        async () => {
          try {
            return await super.analyzeCorrections(request, useStreaming)
          } catch (error: any) {
            throw this.convertToStructuredError(error)
          }
        },
        {
          maxAttempts: 3,
          initialDelay: 1000,
          maxDelay: 10000,
          shouldRetry: (error) => {
            if (error instanceof NetworkError) return true
            if (error instanceof RateLimitError) return true
            if (error instanceof AIProviderError && error.recoverable) return true
            return false
          },
          onRetry: (error, attempt) => {
            this.logger.warn(`AI corrections analysis retry attempt ${attempt}`, {
              error: error.message,
              correctionCount: request.corrections.length
            })
          }
        }
      )

      return result
      
    } catch (error: any) {
      this.logger.error('AI corrections analysis failed', error, {
        correctionCount: request.corrections.length
      })
      
      if (error instanceof ServiceError) {
        throw error
      }
      
      throw new AIProviderError(
        'AI 교정 분석 중 오류가 발생했습니다',
        'CORRECTIONS_ANALYSIS_FAILED',
        { originalError: error }
      )
    }
  }
  
  /**
   * Enhanced initializeClient with better error handling
   */
  async initializeClient(): Promise<void> {
    // this.logger.info('Initializing AI client')
    
    try {
      await super.initializeClient()
      // this.logger.info('AI client initialized successfully', {
      //   model: this.getCurrentModel(),
      //   provider: this.getCurrentProvider()
      // })
    } catch (error: any) {
      this.logger.error('AI client initialization failed', error)
      
      const structuredError = this.convertToStructuredError(error)
      throw structuredError
    }
  }
  
  /**
   * Check availability with proper error handling
   */
  async isAvailable(): Promise<boolean> {
    try {
      const available = await super.isAvailable()
      
      if (!available) {
        this.logger.warn('AI service not available', {
          accessStatus: this.getAccessStatus()
        })
      }
      
      return available
    } catch (error: any) {
      this.logger.error('Failed to check AI availability', error)
      return false
    }
  }
  
  /**
   * Convert legacy errors to structured ServiceError types
   */
  private convertToStructuredError(error: any): ServiceError {
    const message = error.message || '알 수 없는 오류가 발생했습니다'
    
    // Check for rate limit errors
    if (message.includes('429') || message.includes('rate limit')) {
      return new RateLimitError(
        'AI 요청이 너무 많습니다. 잠시 후 다시 시도해주세요.',
        60000, // 1 minute retry
        { originalError: error }
      )
    }
    
    // Check for API key errors
    if (message.includes('API key') || message.includes('authentication') || message.includes('401')) {
      return new AIProviderError(
        'AI API 키가 유효하지 않습니다',
        'INVALID_API_KEY',
        { originalError: error },
        false
      )
    }
    
    // Check for model errors
    if (message.includes('model') || message.includes('not found') || message.includes('not available')) {
      const providerInfo = error.message?.includes('provider:') ? error.message : '';
      return new AIProviderError(
        `AI 모델을 찾을 수 없습니다. ${providerInfo}`,
        'MODEL_NOT_FOUND',
        { originalError: error },
        false
      )
    }
    
    // Check for quota errors
    if (message.includes('quota') || message.includes('limit exceeded')) {
      return new AIProviderError(
        'AI 사용 한도를 초과했습니다',
        'QUOTA_EXCEEDED',
        { originalError: error },
        false
      )
    }
    
    // Check for network errors
    if (message.includes('fetch') || message.includes('network') || message.includes('connection')) {
      return new NetworkError(
        '네트워크 연결 오류가 발생했습니다',
        error.status,
        { originalError: error }
      )
    }
    
    // Check for timeout errors
    if (message.includes('timeout')) {
      return new NetworkError(
        'AI 요청 시간이 초과되었습니다',
        408,
        { originalError: error }
      )
    }
    
    // Default to AIProviderError
    return new AIProviderError(
      message,
      'AI_ERROR',
      { originalError: error },
      true // Assume recoverable by default
    )
  }
  
  /**
   * Enhanced metrics tracking
   */
  async analyzeWithMetrics(
    text: string
  ): Promise<{
    errors: GrammarError[]
    comments: Map<string, AIComment>
    metrics: {
      duration: number
      retries: number
      provider: string
      model: string | null
    }
  }> {
    const startTime = Date.now()
    let retries = 0
    
    // Track retries through event
    const retryHandler = () => retries++
    
    try {
      const result = await this.analyzeText(text, false)
      const duration = Date.now() - startTime
      
      const metrics = {
        duration,
        retries,
        provider: this.getCurrentProvider(),
        model: this.getCurrentModel()
      }
      
      this.logger.debug('AI analysis metrics', metrics)
      
      return { ...result, metrics }
    } catch (error) {
      const duration = Date.now() - startTime
      this.logger.error('AI analysis failed with metrics', error as Error, {
        duration,
        retries
      })
      throw error
    }
  }
}

// Export singleton instance for backward compatibility
let aiAnalysisServiceV2Instance: AIAnalysisServiceV2 | null = null
let lastSettingsHash: string | null = null

function getSettingsHash(settings: AISettings): string {
  return `${settings.provider}:${settings.selectedModel}:${settings.openrouterApiKey || ''}`
}

export function getAIAnalysisServiceV2(settings: AISettings): AIAnalysisServiceV2 {
  const currentHash = getSettingsHash(settings)
  
  // Create new instance if settings have significantly changed
  if (!aiAnalysisServiceV2Instance || lastSettingsHash !== currentHash) {
    aiAnalysisServiceV2Instance = new AIAnalysisServiceV2(settings)
    lastSettingsHash = currentHash
  }
  
  return aiAnalysisServiceV2Instance
}